<template>
  <div
    :class="[
      'formCheckbox',
      {
        'formCheckbox--medium': isMedium,
        'formCheckbox--smallError': smallError,
        'formCheckbox--reverse': isReverse,
      },
    ]"
  >
    <input
      :id="id"
      :name="name"
      type="checkbox"
      :value="value"
      :disabled="disabled"
      :required="required"
      :checked="checkboxState"
      class="formCheckbox__input"
      @change="toggle"
    />
    <label :for="id" class="formCheckbox__label">
      <div class="formCheckbox__check" />
      <div class="formCheckbox__text">
        <div v-if="hasHtml">
          <slot />
        </div>
        <div v-else>
          {{ label }}
        </div>
        <span v-if="available" class="formCheckbox__textNumber">({{ available }})</span>
      </div>
    </label>
  </div>
</template>

<script>
export default {
  name: 'FormCheckbox',
  model: {
    prop: 'model',
    event: 'change',
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    model: {
      type: [String, Boolean, Object, Number, Array],
      default: undefined,
    },
    isReverse: {
      type: Boolean,
      default: false,
    },
    checked: {
      type: Boolean,
      default: false,
    },
    smallError: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Boolean, Object, Number],
      default: false,
    },
    name: {
      type: String,
      default: undefined,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    available: {
      type: Number,
      default: undefined,
    },
    hasHtml: {
      type: Boolean,
      default: false,
    },
    isMedium: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      lv: this.value,
    };
  },
  computed: {
    checkboxState() {
      // eslint-disable-next-line
      if (Array.isArray(this.model)) return this.model.indexOf(this.value) !== -1;
      return this.model || Boolean(this.lv);
    },
    classes() {
      return {
        disabled: this.disabled,
        active: this.checkboxState,
      };
    },
  },
  watch: {
    checked(v) {
      if (v !== this.checkboxState) this.toggle();
    },
    model(v) {
      this.lv = v;
    },
    value(v) {
      this.lv = v;
    },
  },
  mounted() {
    if (this.checked && !this.checkboxState) {
      this.toggle();
    }
  },
  methods: {
    toggle() {
      if (this.disabled) return;
      let v = this.model || this.lv;
      if (Array.isArray(v)) {
        v = [].concat(v);
        const i = v.indexOf(this.value);
        if (i === -1) {
          v.push(this.value);
        } else {
          v.splice(i, 1);
        }
      } else v = !v;
      this.lv = v;
      this.$emit('change', v, this.value);
      this.$emit('emitObject', { name: this.name, value: v });
    },
  },
};
</script>

<style lang="scss">
.formCheckbox {
  $root: &;
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
  position: relative;

  & + .formField__error {
    display: none;
  }

  &.is-invalid {
    & + .formField__error {
      display: inline-flex;
      border: 2px solid $cBadgeProm;
      padding: 1rem;
      border-radius: 4px;

      &::after {
        @include icomoon('wrong');
        font-size: 2.5rem;
        color: $cBadgeProm;
        margin-left: 0.8rem;
      }
    }

    & #{$root}__check {
      border-color: $cBadgeProm;
    }
  }

  &__input {
    display: block;
    width: 1px;
    height: 0;
    position: absolute;
    top: 50%;
    left: 1rem;
    opacity: 0;
    z-index: -1;
  }

  &__label {
    z-index: 0;
    position: relative;
    display: flex;
    align-items: flex-start;
    padding-left: 2.8rem;
    pointer-events: none;
  }

  &__check {
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 1.6rem;
    height: 1.6rem;
    background-color: $cWhite;
    border: 1px solid $cCheckbox;
    border-radius: 2px;
    color: $cBlack;
    cursor: pointer;
    pointer-events: all;
    transition: background-color $tr, color $tr;

    &:before {
      content: '';
      @include icomoon('check');
      font-size: 0.6rem;
      color: $cWhite;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      transition: opacity $tr;
      opacity: 0;
    }

    #{$root}__input:checked + #{$root}__label & {
      background-color: $cSec;
      border: 1px solid $cSec;

      &:before {
        opacity: 1;
      }
    }
  }

  &__text {
    cursor: pointer;
    display: flex;
    font-family: $fMain;
    font-size: 1.4rem;
    line-height: 1.8rem;
    color: $cMainLight;
    pointer-events: all;
    margin-top: -2px;
    z-index: 0;
    position: relative;

    a {
      color: $cMain;
      text-decoration: underline;
    }
  }

  &__textNumber {
    color: $cAddDark02;
    padding-left: 0.5rem;
  }

  &--medium {
    line-height: 2rem;

    #{$root}__check {
      width: 2rem;
      height: 2rem;
      top: -3px;

      &:before {
        font-size: 0.8rem;
      }
    }
  }

  &--smallError {
    &.is-invalid {
      & + .formField__error {
        display: inline-flex;
        border: none;
        padding: 0;
        border-radius: 4px;

        &::after {
          display: none;
        }
      }
    }
  }

  &--reverse {
    #{$root}__label {
      flex-direction: row-reverse;
      padding-right: 2.8rem;
      padding-left: 0;
    }

    #{$root}__check {
      left: unset;
      right: 0;
    }
  }
}
</style>
